import React, { useState, useEffect, useContext, createContext, Dispatch, SetStateAction } from "react";
import { Media } from "react-media-player";
import { useDisclosure } from "@chakra-ui/react";
import PodcastDetailsPath from "content/description.md";
import { parseMarkdown, getPodcastData, GetPodcastData } from "services/utils";

export type PodcastConfig = {
  title: string;
  description: string;
  CID: string;
  thumbnail_url?: string;
  audio_urls?: string[];
  video_urls?: string[];
  episode_number?: number;
  created?: string;
  length: string;
  tags?: string;
  important_details?: string;
  host?: string;
};

export enum ModalType {
  Details = "Details",
  Subscribe = "Subscribe",
  Share = "Share",
  Download = "Download"
}

export enum DataType {
  Content = "Content",
  Metadata = "Metadata",
  Thumbnail = "Thumbnail"
}

export interface IPodcastContext {
  setIsPlaying?: Dispatch<SetStateAction<boolean>>;
  playPodcast?: boolean;
  isPlaying?: boolean;
  volume: boolean;
  setVolume: Dispatch<SetStateAction<boolean>>;
  setPlayPodcast: Dispatch<SetStateAction<boolean>>;
  podcastData: PodcastConfig[];
  setPodcastData: Dispatch<SetStateAction<PodcastConfig[]>>;
  descriptionData: PodcastConfig | null;
  setDescriptionData: Dispatch<SetStateAction<PodcastConfig | null>>;
  playingPodcast: PodcastConfig | null;
  setPlayingPodcast: Dispatch<SetStateAction<PodcastConfig | null>>;
  filteredData: PodcastConfig[] | null;
  setFilteredData: Dispatch<SetStateAction<PodcastConfig[] | null>>;
  modalType: ModalType | null;
  setModalType: Dispatch<SetStateAction<ModalType | null>>;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  modalPodcast: PodcastConfig | null;
  expandedScreenModal: boolean;
  setExpandedScreenModal: Dispatch<SetStateAction<boolean>>;
  setModalPodcast: Dispatch<SetStateAction<PodcastConfig | null>>;
}

export interface PodcastProviderProps {
  children: React.ReactNode;
}

const PodcastContext = createContext<IPodcastContext | undefined>(undefined);

export const PodcastProvider = ({ children }: PodcastProviderProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  // state
  const [playPodcast, setPlayPodcast] = useState(false);
  const [volume, setVolume] = useState(false);
  const [podcastData, setPodcastData] = useState<PodcastConfig[]>([]);
  const [descriptionData, setDescriptionData] = useState<PodcastConfig | null>(null);
  const [playingPodcast, setPlayingPodcast] = useState<PodcastConfig | null>(null);
  const [filteredData, setFilteredData] = useState<PodcastConfig[] | null>(null);
  const [modalType, setModalType] = useState<ModalType | null>(null);
  const [modalPodcast, setModalPodcast] = useState<PodcastConfig | null>(null);
  const [expandedScreenModal, setExpandedScreenModal] = useState(false);

  useEffect(() => {
    async function getPosts() {
      const episodes: GetPodcastData[] = [];
      let i = 1;
      while (i > 0) {
        try {
          const content = await import(`content/episodes/ep${i}.md`);
          const response = await fetch(content?.default);
          const text = await response.text();
          episodes.push({ episodeNumber: i, episodeContent: text });
        } catch {
          break;
        }
        i++;
      }
      const promises = episodes.map(episode => getPodcastData(episode));
      Promise.all(promises).then(values => {
        setPodcastData(values.reverse());
      });
    }

    fetch(PodcastDetailsPath)
      .then(response => response.text())
      .then(text => {
        setDescriptionData(parseMarkdown(text));
      });
    getPosts();
  }, []);

  return (
    <PodcastContext.Provider
      value={{
        playPodcast,
        setPlayPodcast,
        setPodcastData,
        podcastData,
        descriptionData,
        setDescriptionData,
        playingPodcast,
        setPlayingPodcast,
        setVolume,
        volume,
        setFilteredData,
        filteredData,
        modalType,
        setModalType,
        isOpen,
        onOpen,
        onClose,
        modalPodcast,
        setModalPodcast,
        expandedScreenModal,
        setExpandedScreenModal
      }}
    >
      <Media>
        <>{children}</>
      </Media>
    </PodcastContext.Provider>
  );
};

export const usePodcastProvider = () => {
  const context = useContext(PodcastContext);

  if (!context) {
    throw new Error(`usePodcastContext must be used inside PodcastContextProvider`);
  }

  return context;
};
